import { read_cookie } from 'sfcookies';
import { hostUrl, UseUrlManagerCleanUrl } from '../utils/url-manager';

/**
 * HQ-API's queues, Stage 3 of the HQ queue screens.
 *
 * Direct to HQ-API rather than through a react-admin data provider, for the reason the change log
 * gives: the providers turn a refusal into an empty result (HQ-06), and "I could not read the
 * queues" must never render as "nothing is queued". That distinction is the whole point of this
 * screen, so it is drawn in the types as well: a count is `number | null` and a section carries its
 * own `available` flag.
 *
 * Reading needs `HQ_System::queues`. There is nothing to change here yet; the controls arrive with
 * `HQ_System::queues_control` in Stage 4.
 */

export const QUEUES_PERMISSION: string = 'HQ_System::queues';

/**
 * Retrying, deleting and pausing. A **sibling** of `HQ_System::queues` in the tree, not a child, so
 * it can be withheld from an admin who can still open the screen. HQ-API enforces it; this only
 * decides what to show.
 */
export const QUEUES_CONTROL_PERMISSION: string = 'HQ_System::queues_control';

/** One queue in `messenger_messages`. Only queues currently holding something appear. */
export type QueueDepth = {
    queue: string,
    total: number,
    /** Waiting to be picked up. */
    waiting: number,
    /** Held back until a retry is due. */
    delayed: number,
    /** Taken by a worker and not yet finished. A number that does not fall is a worker that died holding it. */
    inFlight: number,
    /** ISO 8601, UTC. */
    oldestAt: string | null,
    oldestAgeSeconds: number | null,
};

export type TransportSection = {
    available: boolean,
    queues: QueueDepth[],
    /** HQ-API's own words when it could not read the table. */
    reason: string | null,
};

/**
 * A transport a worker could be consuming.
 *
 * `consumed` is `true` or `null`, never `false`: no heartbeat means nobody has reported, which on a
 * release predating the heartbeat, or with Redis unreachable, is not the same as nobody consuming
 * it. The screen says "no worker reporting", not "stopped".
 */
export type TransportWorker = {
    transport: string,
    consumed: boolean | null,
    lastSeenAt: string | null,
    lastSeenAgeSeconds: number | null,
};

export type ScheduleHealth = {
    name: string,
    transport: string,
    /** How Symfony describes each trigger, such as "every 1 minute". Empty when none are set. */
    triggers: string[],
    lastRunAt: string | null,
    lastRunAgeSeconds: number | null,
    consumed: boolean | null,
    workerLastSeenAt: string | null,
    workerLastSeenAgeSeconds: number | null,
    /**
     * Held by a maintenance window rather than stopped. Separate from `pausedByAdmin` because they
     * are different listeners with different lifetimes: a window releases its own when it ends, an
     * admin's lasts until someone resumes it. One "paused" flag would send an admin to the wrong
     * switch.
     */
    pausedByMaintenance: boolean,
    pausedByAdmin: boolean,
};

/** A message that ran out of retries. The failed queue is the last copy there is. */
export type FailedMessage = {
    id: string | null,
    class: string,
    /** Where it will go back to if it is retried. Null means the origin was not recorded. */
    originalTransport: string | null,
    failedAt: string | null,
    attempts: number | null,
    errorClass: string | null,
    error: string | null,
};

export type FailedSection = {
    available: boolean,
    messages: FailedMessage[],
    reason: string | null,
};

/** What an action did. Some ids are refused, and a partial result is a success, not an error. */
export type ActionResult = {
    done: string[],
    refused: Record<string, string>,
};

/** Work waiting in an ordinary table, drained by a sweep rather than by a worker. */
export type WorkBacklog = {
    key: string,
    title: string,
    /** The schedule that clears it, so the two can be read side by side. */
    drainedBy: string,
    /** Null is "could not be counted", which is not none. */
    count: number | null,
};

export type QueuesReport = {
    /** ISO 8601, UTC. */
    generatedAt: string,
    transports: TransportSection,
    workers: TransportWorker[],
    schedules: ScheduleHealth[],
    backlogs: WorkBacklog[],
};

const NO_TRANSPORTS: TransportSection = { available: false, queues: [], reason: null };

const text = (value: unknown): string => typeof value === 'string' ? value : '';

const textOrNull = (value: unknown): string | null =>
    typeof value === 'string' && value !== '' ? value : null;

const count = (value: unknown): number => typeof value === 'number' && Number.isFinite(value) ? value : 0;

const countOrNull = (value: unknown): number | null =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;

/** True or null only. Anything else, including `false`, reads as "nobody has reported". */
const reported = (value: unknown): boolean | null => value === true ? true : null;

const toQueue = (raw: Record<string, unknown>): QueueDepth => ({
    queue: text(raw.queue),
    total: count(raw.total),
    waiting: count(raw.waiting),
    delayed: count(raw.delayed),
    inFlight: count(raw.inFlight),
    oldestAt: textOrNull(raw.oldestAt),
    oldestAgeSeconds: countOrNull(raw.oldestAgeSeconds),
});

const toTransports = (raw: unknown): TransportSection => {
    if (typeof raw !== 'object' || raw === null) {
        return NO_TRANSPORTS;
    }

    const section = raw as Record<string, unknown>;

    return {
        available: section.available === true,
        reason: textOrNull(section.reason),
        queues: Array.isArray(section.queues)
            ? section.queues.map((queue): QueueDepth => toQueue(queue as Record<string, unknown>))
            : [],
    };
};

const toWorker = (raw: Record<string, unknown>): TransportWorker => ({
    transport: text(raw.transport),
    consumed: reported(raw.consumed),
    lastSeenAt: textOrNull(raw.lastSeenAt),
    lastSeenAgeSeconds: countOrNull(raw.lastSeenAgeSeconds),
});

const toSchedule = (raw: Record<string, unknown>): ScheduleHealth => ({
    name: text(raw.name),
    transport: text(raw.transport),
    triggers: Array.isArray(raw.triggers) ? raw.triggers.map(text).filter((one): boolean => one !== '') : [],
    lastRunAt: textOrNull(raw.lastRunAt),
    lastRunAgeSeconds: countOrNull(raw.lastRunAgeSeconds),
    consumed: reported(raw.consumed),
    workerLastSeenAt: textOrNull(raw.workerLastSeenAt),
    workerLastSeenAgeSeconds: countOrNull(raw.workerLastSeenAgeSeconds),
    pausedByMaintenance: raw.pausedByMaintenance === true,
    pausedByAdmin: raw.pausedByAdmin === true,
});

const toFailed = (raw: Record<string, unknown>): FailedMessage => ({
    id: textOrNull(raw.id),
    class: text(raw.class),
    originalTransport: textOrNull(raw.originalTransport),
    failedAt: textOrNull(raw.failedAt),
    attempts: countOrNull(raw.attempts),
    errorClass: textOrNull(raw.errorClass),
    error: textOrNull(raw.error),
});

const toBacklog = (raw: Record<string, unknown>): WorkBacklog => ({
    key: text(raw.key),
    title: text(raw.title),
    drainedBy: text(raw.drainedBy),
    count: countOrNull(raw.count),
});

const list = <T, >(raw: unknown, map: (row: Record<string, unknown>) => T): T[] =>
    Array.isArray(raw) ? raw.map((row): T => map(row as Record<string, unknown>)) : [];

const toReport = (raw: unknown): QueuesReport => {
    const data = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

    return {
        generatedAt: text(data.generatedAt),
        transports: toTransports(data.transports),
        workers: list(data.workers, toWorker),
        schedules: list(data.schedules, toSchedule),
        backlogs: list(data.backlogs, toBacklog),
    };
};

/**
 * How long ago, in the shortest form that is still honest.
 *
 * Seconds below a minute because a queue's age is read at that resolution when something is wrong.
 */
export const describeAge = (seconds: number | null): string => {
    if (seconds === null) {
        return 'never';
    }
    if (seconds < 60) {
        return seconds + 's ago';
    }
    if (seconds < 3600) {
        return Math.floor(seconds / 60) + 'm ago';
    }
    if (seconds < 86400) {
        return Math.floor(seconds / 3600) + 'h ago';
    }

    return Math.floor(seconds / 86400) + 'd ago';
};

/**
 * What to say about a transport nobody has reported consuming.
 *
 * Deliberately not "stopped". Until every environment runs a release carrying the heartbeat, a
 * silent transport is as likely to be an old release as a missing worker, and telling an admin a
 * queue is dead when it is not is worse than telling them nothing.
 */
export const describeWorker = (worker: Pick<TransportWorker, 'consumed' | 'lastSeenAgeSeconds'>): string =>
    worker.consumed === true
        ? 'Worker seen ' + describeAge(worker.lastSeenAgeSeconds)
        : 'No worker reporting';

const request = async (path: string, init: RequestInit): Promise<unknown> => {
    const token: unknown = read_cookie('auth');
    if (typeof token !== 'string' || token.length === 0) {
        throw new Error('Not signed in');
    }

    const response: Response = await fetch(new Request(UseUrlManagerCleanUrl({ url: `${ hostUrl }${ path }` }), {
        ...init,
        headers: new Headers({ 'Content-Type': 'application/json', 'token': token, 'hq': 'hq' }),
    }));

    let body: { status?: unknown, message?: unknown, data?: unknown } | null = null;
    try {
        body = await response.json();
    } catch {
        body = null;
    }
    if (!response.ok || body?.status !== 'success') {
        // HQ-API's own reason when it gave one: the refusal for a missing permission, or the
        // sentence it answers a bad request with. Both are written to be shown to an admin.
        throw new Error(typeof body?.message === 'string' && body.message !== ''
            ? body.message
            : 'The queues could not be reached');
    }

    return body.data;
};

/** Everything the Queues screen shows. Throws on any failure. */
export const loadQueues = async (): Promise<QueuesReport> =>
    toReport(await request('hq/queues', { method: 'GET' }));

/** The failed queue, read separately because it is paged and the rest is not. */
export const loadFailedMessages = async (): Promise<FailedSection> => {
    const raw = await request('hq/queues/failed', { method: 'GET' });
    const section = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

    return {
        available: section.available === true,
        reason: textOrNull(section.reason),
        messages: list(section.messages, toFailed),
    };
};

/**
 * HQ-API names both what it did and what it would not do, under different keys per action. A
 * partial result arrives here as a success, because some ids being gone is the ordinary case when
 * two admins have the screen open.
 */
const toActionResult = (raw: unknown, key: string): ActionResult => {
    const data = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
    const refused = data.refused;

    return {
        done: Array.isArray(data[key]) ? (data[key] as unknown[]).map(text).filter((one): boolean => one !== '') : [],
        refused: typeof refused === 'object' && refused !== null
            ? Object.fromEntries(Object.entries(refused).map(([id, why]): [string, string] => [id, text(why)]))
            : {},
    };
};

/** Put these back on the transports they came from. Does not run their handlers. */
export const retryFailedMessages = async (ids: string[]): Promise<ActionResult> =>
    toActionResult(await request('hq/queues/failed/retry', { method: 'POST', body: JSON.stringify({ ids }) }), 'retried');

/** Throw these away. There is no undo. */
export const deleteFailedMessages = async (ids: string[]): Promise<ActionResult> =>
    toActionResult(await request('hq/queues/failed/delete', { method: 'POST', body: JSON.stringify({ ids }) }), 'deleted');

/** Stop or resume one schedule's ticks. Returns every schedule an admin currently has paused. */
export const setSchedulePaused = async (name: string, paused: boolean): Promise<string[]> => {
    const raw = await request('hq/queues/schedules/pause', {
        method: 'POST',
        body: JSON.stringify({ name, paused }),
    });
    const data = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;

    return Array.isArray(data.paused) ? data.paused.map(text).filter((one): boolean => one !== '') : [];
};
